import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { home, nav, footer as footerData, languages, countries } from "@/content/site";

/* ============================================================
   THEME B — "Obsidian Editorial"
   Self-contained route (/theme-b) with its own header & footer.
   Dark obsidian canvas, warm cream type, acid-lime accent,
   Fraunces serif display + Space Grotesk body.
============================================================ */

// Load fonts once (scoped style tag so it doesn't leak globals)
function ThemeFonts() {
  return (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,300;0,9..144,500;0,9..144,700;1,9..144,400&family=Space+Grotesk:wght@300;400;500;600&display=swap');
      .theme-b { background: #0E0F14; color: #EFE8D9; font-family: 'Space Grotesk', system-ui, sans-serif; }
      .theme-b .serif { font-family: 'Fraunces', Georgia, serif; font-optical-sizing: auto; }
      .theme-b .mono { font-family: 'JetBrains Mono', ui-monospace, monospace; }
      .theme-b .lime { color: #C8FF4B; }
      .theme-b .bg-lime { background: #C8FF4B; color: #0E0F14; }
      .theme-b .bg-cream { background: #EFE8D9; color: #0E0F14; }
      .theme-b .divider { border-color: #2A2C35; }
      .theme-b a { color: inherit; }
      @keyframes themeb-float { 0% { opacity: 0; transform: translateY(14px); } 100% { opacity: 1; transform: translateY(0); } }
      .theme-b .float-in { animation: themeb-float 0.9s cubic-bezier(0.22, 1, 0.36, 1) both; }
      @keyframes themeb-draw { from { transform: scaleX(0); } to { transform: scaleX(1); } }
      .theme-b .draw { transform-origin: left center; animation: themeb-draw 1.2s ease-out both; }
      .theme-b ::selection { background: #C8FF4B; color: #0E0F14; }
      .theme-b .marquee-inner { animation: marquee 50s linear infinite; }
    `}</style>
  );
}

function AltLogo({ size = 30 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 60 80" fill="none" stroke="#EFE8D9" strokeWidth="2">
      <path d="M 10 6 L 10 74" />
      <path d="M 10 6 L 50 6" />
      <path d="M 10 74 L 50 74" />
      <path d="M 50 6 L 50 28" />
      <path d="M 50 52 L 50 74" />
      <path d="M 22 18 L 22 62" />
      <path d="M 38 18 L 38 62" />
      <path d="M 22 40 L 38 40" />
    </svg>
  );
}

function Header() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => setOpen(false), [pathname]);

  return (
    <header className="sticky top-0 z-40 border-b divider bg-[#0E0F14]/90 backdrop-blur-xl" data-testid="themeb-header">
      <div className="max-w-[1600px] mx-auto flex items-center justify-between px-6 md:px-10 lg:px-14 h-20">
        <Link to="/theme-b" className="flex items-center gap-3">
          <AltLogo size={28} />
          <span className="serif text-2xl tracking-tight">Hubinall</span>
        </Link>

        <nav className="hidden xl:flex items-center gap-8">
          {nav.slice(1).map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                "text-[13px] tracking-tight hover:lime transition-colors " + (isActive ? "lime" : "text-[#EFE8D9]/80")
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden xl:flex items-center gap-4">
          <Link to="/" className="mono text-[10px] uppercase tracking-[0.22em] text-[#EFE8D9]/60 hover:text-[#EFE8D9] border border-[#2A2C35] px-3 py-2">
            ← View Theme C
          </Link>
          <Link
            to="/contact?form=call"
            className="mono text-[11px] uppercase tracking-[0.22em] bg-lime px-5 py-3 hover:bg-[#EFE8D9] transition-colors"
          >
            Request a Call
          </Link>
        </div>

        <button onClick={() => setOpen((v) => !v)} className="xl:hidden w-10 h-10 flex items-center justify-center border divider">
          <span className="flex flex-col gap-1.5">
            <span className="block w-4 h-px bg-[#EFE8D9]" />
            <span className="block w-4 h-px bg-[#EFE8D9]" />
            <span className="block w-4 h-px bg-[#EFE8D9]" />
          </span>
        </button>
      </div>
      {open && (
        <div className="xl:hidden border-t divider">
          <div className="px-6 py-5 flex flex-col">
            {nav.map((n) => (
              <NavLink key={n.path} to={n.path} className="py-3 border-b divider text-base">
                {n.label}
              </NavLink>
            ))}
            <Link to="/" className="mono text-xs uppercase tracking-widest lime pt-4">← View Theme A</Link>
          </div>
        </div>
      )}
    </header>
  );
}

function Footer() {
  return (
    <footer className="border-t divider" data-testid="themeb-footer">
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-14 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 pb-14 border-b divider">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-7">
              <AltLogo size={30} />
              <span className="serif text-2xl tracking-tight">Hubinall</span>
            </div>
            <p className="text-sm leading-relaxed text-[#EFE8D9]/65 max-w-md">{footerData.brand}</p>
            <p className="mono text-[11px] uppercase tracking-[0.22em] lime mt-6">{footerData.brandNote}</p>
          </div>
          {footerData.columns.map((col) => (
            <div key={col.title} className="lg:col-span-2">
              <div className="mono text-[10px] uppercase tracking-[0.22em] text-[#EFE8D9]/40 mb-5">{col.title}</div>
              <ul className="space-y-3">
                {col.links.map((l) => (
                  <li key={l.to}>
                    <Link to={l.to} className="text-sm text-[#EFE8D9]/80 hover:lime">
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          <div className="lg:col-span-1">
            <div className="mono text-[10px] uppercase tracking-[0.22em] text-[#EFE8D9]/40 mb-5">Contact</div>
            <ul className="space-y-3 text-sm text-[#EFE8D9]/80">
              <li><a href="mailto:hi@hubinall.com" className="hover:lime">hi@hubinall.com</a></li>
            </ul>
          </div>
        </div>
        <div className="pt-8 flex flex-col lg:flex-row justify-between gap-6 items-start lg:items-center">
          <div className="flex flex-wrap gap-x-5 gap-y-2 mono text-[10px] uppercase tracking-[0.18em] text-[#EFE8D9]/45">
            {footerData.legalLinks.slice(0, 7).map((l) => (
              <Link key={l.to} to={l.to.startsWith("#") ? "/legal/privacy" : l.to} className="hover:lime">
                {l.label}
              </Link>
            ))}
          </div>
          <div className="mono text-[10px] uppercase tracking-[0.22em] text-[#EFE8D9]/40">{footerData.copyright}</div>
        </div>
      </div>
    </footer>
  );
}

// ============================= PAGE =============================
export default function HomeAlt() {
  return (
    <div className="theme-b min-h-screen" data-testid="themeb-home">
      <ThemeFonts />
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden">
        {/* Soft glow */}
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -top-40 -right-40 w-[700px] h-[700px] rounded-full opacity-[0.08]"
               style={{ background: "radial-gradient(circle, #C8FF4B 0%, transparent 60%)" }} />
        </div>

        <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-14 pt-20 md:pt-28 pb-24 relative">
          <div className="flex items-center gap-3 mb-12 float-in">
            <div className="h-px bg-[#EFE8D9]/40 w-10 draw" />
            <span className="mono text-[10px] uppercase tracking-[0.35em] lime">v.2026 · Theme B preview</span>
          </div>

          <h1 className="serif font-light text-[4rem] sm:text-[5.5rem] md:text-[7rem] lg:text-[9rem] leading-[0.88] tracking-[-0.03em] mb-14 max-w-[1300px] float-in"
              data-testid="themeb-hero">
            Business <em className="serif italic lime font-normal">infrastructure</em>,
            <br />
            coordinated<span className="lime">.</span>
          </h1>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <p className="lg:col-span-6 text-lg md:text-2xl leading-[1.35] text-[#EFE8D9]/85 max-w-2xl">
              {home.hero.subheadline}
            </p>
            <div className="lg:col-span-4 lg:col-start-9">
              <p className="text-sm leading-[1.7] text-[#EFE8D9]/55 mb-8">{home.hero.support}</p>
              <div className="flex flex-wrap gap-3">
                <Link to="/solutions"
                      className="group inline-flex items-center gap-3 bg-lime px-5 py-3.5 mono text-[11px] uppercase tracking-[0.22em] hover:bg-[#EFE8D9]">
                  Explore Solutions
                  <span className="transition-transform group-hover:translate-x-1">→</span>
                </Link>
                <Link to="/contact?form=call"
                      className="group inline-flex items-center gap-3 border border-[#EFE8D9]/40 px-5 py-3.5 mono text-[11px] uppercase tracking-[0.22em] hover:border-lime hover:lime">
                  Request a Call <span className="transition-transform group-hover:translate-x-1">→</span>
                </Link>
              </div>
            </div>
          </div>

          {/* Country strip */}
          <div className="mt-20 pt-8 border-t divider flex flex-col md:flex-row md:items-center gap-6 justify-between">
            <span className="mono text-[10px] uppercase tracking-[0.3em] text-[#EFE8D9]/55">
              Operating in
            </span>
            <div className="flex flex-wrap gap-3">
              {countries.map((c) => (
                <span key={c}
                      className="serif italic text-2xl md:text-3xl px-5 py-2 border divider hover:border-[#C8FF4B] hover:lime transition-colors cursor-default">
                  {c}
                </span>
              ))}
            </div>
            <span className="mono text-[10px] uppercase tracking-[0.3em] text-[#EFE8D9]/55">
              07 layers · 12 services · 08 partners
            </span>
          </div>
        </div>
      </section>

      {/* AUDIENCE CARDS */}
      <section className="border-t divider">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-14 py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16">
            <div className="lg:col-span-4">
              <div className="mono text-[10px] uppercase tracking-[0.3em] lime mb-5"> who it's for</div>
              <h2 className="serif text-5xl md:text-6xl leading-[0.95] tracking-tight">
                Three <em className="italic">audiences</em>,<br />one coordination layer.
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#2A2C35]">
            {home.audienceCards.map((c, i) => (
              <Link
                to={c.to}
                key={c.title}
                className="group bg-[#0E0F14] p-10 md:p-12 hover:bg-[#14161D] transition-colors"
                data-testid={`themeb-audience-${i}`}
              >
                <div className="flex items-baseline justify-between mb-10">
                  <span className="mono text-[11px] uppercase tracking-[0.3em] lime">{String(i + 1).padStart(2, "0")}</span>
                  <span className="serif italic text-xl text-[#EFE8D9]/40 group-hover:lime">↗</span>
                </div>
                <h3 className="serif text-3xl md:text-4xl mb-6 leading-tight">{c.title}</h3>
                <p className="text-sm md:text-base leading-relaxed text-[#EFE8D9]/65">{c.body}</p>
                <div className="mt-10 mono text-[11px] uppercase tracking-[0.25em] lime opacity-0 group-hover:opacity-100 transition-opacity">
                  Explore pathway →
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT IS HUBINALL */}
      <section className="border-t divider bg-[#0B0C10]">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-14 py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-20">
            <div className="lg:col-span-5">
              <div className="mono text-[10px] uppercase tracking-[0.3em] lime mb-5"> what it is</div>
              <h2 className="serif text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
                {home.whatIsHubinall.heading.split(" ").map((w, i) =>
                  ["coordination", "operating"].includes(w.toLowerCase())
                    ? <em key={i} className="italic lime">{w} </em>
                    : <span key={i}>{w} </span>
                )}
              </h2>
            </div>
            <div className="lg:col-span-6 lg:col-start-7 text-base md:text-lg leading-[1.7] text-[#EFE8D9]/70">
              {home.whatIsHubinall.body}
            </div>
          </div>

          {/* Layer list — large editorial rows */}
          <div className="border-t divider">
            {home.whatIsHubinall.blocks.map((b, i) => (
              <div key={b.title}
                   className="group grid grid-cols-12 gap-6 py-10 border-b divider hover:bg-[#14161D] transition-colors">
                <div className="col-span-1 mono text-[11px] uppercase tracking-[0.22em] lime pt-3">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <h3 className="col-span-11 md:col-span-4 serif text-2xl md:text-3xl leading-tight group-hover:lime transition-colors">
                  {b.title}
                </h3>
                <p className="col-span-12 md:col-span-7 text-sm md:text-base leading-relaxed text-[#EFE8D9]/65">
                  {b.body}
                </p>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap gap-3 mt-14">
            <Link to="/services" className="inline-flex items-center gap-3 bg-lime px-5 py-3.5 mono text-[11px] uppercase tracking-[0.22em] hover:bg-[#EFE8D9]">
              View All Services →
            </Link>
            <Link to="/platform" className="inline-flex items-center gap-3 border border-[#EFE8D9]/40 px-5 py-3.5 mono text-[11px] uppercase tracking-[0.22em] hover:border-lime hover:lime">
              Explore the Platform →
            </Link>
          </div>
        </div>
      </section>

      {/* REGIONS — tall editorial split */}
      <section className="border-t divider">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-14 py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16">
            <div className="lg:col-span-6">
              <div className="mono text-[10px] uppercase tracking-[0.3em] lime mb-5"> regions</div>
              <h2 className="serif text-5xl md:text-6xl leading-[0.95] tracking-tight">
                {home.regions.heading.replace("operating environments", "")}
                <em className="italic lime">operating environments.</em>
              </h2>
            </div>
            <div className="lg:col-span-5 lg:col-start-8 text-base md:text-lg leading-[1.7] text-[#EFE8D9]/65">
              {home.regions.body}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#2A2C35] border divider">
            {home.regions.blocks.map((b, i) => (
              <div key={b.title}
                   className="bg-[#0E0F14] p-10 md:p-12 relative group hover:bg-[#14161D] transition-colors">
                <div className="serif text-[5rem] md:text-[6rem] leading-none tracking-tighter text-[#EFE8D9]/15 group-hover:lime group-hover:opacity-30 transition-all absolute top-6 right-6">
                  0{i + 1}
                </div>
                <div className="mono text-[11px] uppercase tracking-[0.25em] lime mb-6 relative">Region</div>
                <h3 className="serif text-4xl md:text-5xl mb-6 leading-tight relative">
                  <em className="italic">{b.title}</em>
                </h3>
                <div className="h-px bg-[#EFE8D9]/20 w-10 mb-6 group-hover:w-24 group-hover:bg-[#C8FF4B] transition-all duration-500" />
                <p className="text-sm md:text-base leading-relaxed text-[#EFE8D9]/65 relative">{b.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PARTNERS — marquee */}
      <section className="border-t divider overflow-hidden">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-14 pt-24 pb-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
            <div className="lg:col-span-6">
              <div className="mono text-[10px] uppercase tracking-[0.3em] lime mb-5"> ecosystem</div>
              <h2 className="serif text-5xl md:text-6xl leading-[0.95] tracking-tight">
                A growing network of <em className="italic lime">core</em> and <em className="italic">strategic</em> partners.
              </h2>
            </div>
            <div className="lg:col-span-5 lg:col-start-8 text-base md:text-lg leading-[1.7] text-[#EFE8D9]/65">
              {home.partnersPreview.body}
            </div>
          </div>
        </div>

        <div className="border-y divider py-10 overflow-hidden">
          <div className="flex whitespace-nowrap marquee-inner">
            {[...home.partnersPreview.core, ...home.partnersPreview.strategic, ...home.partnersPreview.core, ...home.partnersPreview.strategic].map((p, i) => (
              <div key={i} className="flex items-center gap-12 px-8">
                <span className="serif text-4xl md:text-6xl tracking-tight">{p}</span>
                <span className="lime text-2xl">✦</span>
              </div>
            ))}
          </div>
        </div>

        <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-14 py-12 grid grid-cols-1 md:grid-cols-2 gap-10">
          <div>
            <div className="mono text-[10px] uppercase tracking-[0.3em] lime mb-3">Core</div>
            <p className="serif text-xl text-[#EFE8D9]/85">{home.partnersPreview.core.join(" · ")}</p>
          </div>
          <div>
            <div className="mono text-[10px] uppercase tracking-[0.3em] lime mb-3">Strategic ecosystem</div>
            <p className="serif text-xl text-[#EFE8D9]/85">{home.partnersPreview.strategic.join(" · ")}</p>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="border-t divider bg-lime text-[#0E0F14]" data-testid="themeb-cta">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-14 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-8">
            <div className="mono text-[10px] uppercase tracking-[0.3em] mb-8"> next step</div>
            <h2 className="serif text-5xl md:text-7xl lg:text-8xl leading-[0.92] tracking-tight">
              Ready to coordinate your <em className="italic">next operational layer</em>?
            </h2>
          </div>
          <div className="lg:col-span-4 flex flex-col justify-end gap-5">
            <p className="text-base md:text-lg leading-[1.6]">{home.finalCta.body}</p>
            <div className="flex flex-col gap-3">
              <Link to="/contact?form=setup"
                    className="group flex items-center justify-between bg-[#0E0F14] text-[#EFE8D9] px-6 py-5 mono text-xs uppercase tracking-[0.22em] hover:bg-[#EFE8D9] hover:text-[#0E0F14] transition-colors">
                Start Your Setup <span className="transition-transform group-hover:translate-x-1">→</span>
              </Link>
              <Link to="/solutions"
                    className="group flex items-center justify-between border border-[#0E0F14] px-6 py-5 mono text-xs uppercase tracking-[0.22em] hover:bg-[#0E0F14] hover:text-[#EFE8D9] transition-colors">
                Explore Solutions <span className="transition-transform group-hover:translate-x-1">→</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      {/* Floating theme toggle */}
      <div className="fixed bottom-6 left-6 z-50 flex items-center gap-2 bg-[#0E0F14] border divider px-4 py-3 mono text-[10px] uppercase tracking-[0.25em]" data-testid="theme-toggle">
        <span className="lime">●</span>
        <span className="text-[#EFE8D9]/70">Theme B · Obsidian</span>
        <Link to="/" className="lime hover:underline ml-3">Switch → A</Link>
      </div>
    </div>
  );
}
